/**
 * Typed client for the Draw Earth API (see packages/shared/src/api.ts).
 */
import type {
  ActivityBatch,
  AvatarResponse,
  DrawOp,
  DrawResponse,
  CellTile,
  GlobalLivePainter,
  InkState,
  LivePainter,
  Me,
  MetaEventRequest,
  NotificationItem,
  NotificationPrefs,
  ParametersResponse,
  PlaybackDates,
  ReportReason,
  SiteStats,
  TileInfo,
  TileRegion,
} from '@drawearth/shared';
import { decodeGlobeCells, type GlobeCellRecord } from '@drawearth/shared';
import { API_URL } from '../config.ts';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message?: string,
    public details?: unknown,
  ) {
    super(message ?? code);
  }
}

export type TokenProvider = () => Promise<string | null>;

export class ApiClient {
  constructor(
    private base = API_URL,
    private tokenProvider: TokenProvider = async () => null,
  ) {}

  setTokenProvider(p: TokenProvider): void {
    this.tokenProvider = p;
  }

  private async request<T>(path: string, init: RequestInit = {}, opts: { auth?: boolean; raw?: boolean } = {}): Promise<T> {
    const headers = new Headers(init.headers ?? {});
    if (init.body && typeof init.body === 'string' && !headers.has('content-type')) headers.set('content-type', 'application/json');
    if (opts.auth !== false) {
      const token = await this.tokenProvider();
      if (token) headers.set('authorization', `Bearer ${token}`);
    }
    const res = await fetch(`${this.base}${path}`, { ...init, headers });
    if (!res.ok) {
      let body: { error?: string; message?: string; details?: unknown } = {};
      try {
        body = await res.json();
      } catch {
        // non-JSON error
      }
      throw new ApiError(res.status, body.error ?? `http_${res.status}`, body.message, body.details);
    }
    if (opts.raw) return res as unknown as T;
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  // --- public ---
  parameters(): Promise<ParametersResponse> {
    return this.request('/v1/parameters', {}, { auth: false });
  }

  async tilePixels(tile: string): Promise<Uint8Array | null> {
    const res = await fetch(`${this.base}/v1/tiles/${tile}`);
    if (res.status === 404) return null;
    if (!res.ok) throw new ApiError(res.status, `http_${res.status}`);
    const buf = new Uint8Array(await res.arrayBuffer());
    return buf.length === 65536 ? buf : null;
  }

  async playback(tile: string, date: string, excludeAgents = false): Promise<Uint8Array> {
    const res = await fetch(`${this.base}/v1/tiles/${tile}/playback?date=${date}${excludeAgents ? '&excludeAgents=1' : ''}`);
    if (!res.ok) throw new ApiError(res.status, `http_${res.status}`);
    return new Uint8Array(await res.arrayBuffer());
  }

  dates(tile: string): Promise<PlaybackDates> {
    return this.request(`/v1/tiles/${tile}/dates`, {}, { auth: false });
  }

  tileInfo(tile: string): Promise<TileInfo> {
    return this.request(`/v1/tiles/${tile}/info`, {}, { auth: false });
  }

  tileLive(tile: string): Promise<{ tile: string; painters: LivePainter[] }> {
    return this.request(`/v1/tiles/${tile}/live`, {}, { auth: false });
  }

  setLive(live: boolean): Promise<{ liveUntil: number | null }> {
    return this.request('/v1/users/me/live', { method: 'PUT', body: JSON.stringify({ live }) });
  }

  async globeCells(): Promise<GlobeCellRecord[]> {
    const res = await fetch(`${this.base}/v1/globe/cells`);
    if (!res.ok) return [];
    return decodeGlobeCells(new Uint8Array(await res.arrayBuffer()));
  }

  async globeTiles(): Promise<GlobeCellRecord[]> {
    const res = await fetch(`${this.base}/v1/globe/tiles`);
    if (!res.ok) return [];
    return decodeGlobeCells(new Uint8Array(await res.arrayBuffer()));
  }

  globeTextureUrl(): string {
    return `${this.base}/v1/globe/texture`;
  }

  activity(): Promise<ActivityBatch> {
    return this.request('/v1/activity', {}, { auth: false });
  }

  stats(): Promise<SiteStats> {
    return this.request('/v1/stats', {}, { auth: false });
  }

  /** Painted tiles inside an activity cell, busiest first (public aggregate). */
  cellTiles(cell: string): Promise<{ tiles: CellTile[] }> {
    return this.request(`/v1/cells/${encodeURIComponent(cell)}/tiles`, {}, { auth: false });
  }

  live(): Promise<{ painters: GlobalLivePainter[] }> {
    return this.request('/v1/live', {}, { auth: false });
  }

  tilePngUrl(tile: string): string {
    return `${this.base}/v1/tiles/${tile}.png`;
  }

  tileThumbUrl(tile: string): string {
    return `${this.base}/v1/tiles/${tile}/thumb.png`;
  }

  // --- auth / identity ---
  devToken(uid: string): Promise<{ uid: string; token: string }> {
    return this.request('/dev/token', { method: 'POST', body: JSON.stringify({ uid }) }, { auth: false });
  }

  signUp(): Promise<Me> {
    return this.request('/v1/users/me', { method: 'POST', body: JSON.stringify({ ageAttested: true }) });
  }

  me(): Promise<Me> {
    return this.request('/v1/users/me');
  }

  ink(): Promise<InkState> {
    return this.request('/v1/users/me/ink');
  }

  rerollHandle(): Promise<{ handle: string }> {
    return this.request('/v1/users/me/handle', { method: 'POST' });
  }

  setAvatar(avatarBase64: string): Promise<AvatarResponse> {
    return this.request('/v1/users/me/avatar', { method: 'PUT', body: JSON.stringify({ avatar: avatarBase64 }) });
  }

  setNotificationPrefs(prefs: Partial<NotificationPrefs>): Promise<NotificationPrefs> {
    return this.request('/v1/users/me/notification-prefs', { method: 'PUT', body: JSON.stringify(prefs) });
  }

  addPushToken(platform: 'ios' | 'android' | 'web', token: string): Promise<{ ok: true }> {
    return this.request('/v1/users/me/push-tokens', { method: 'POST', body: JSON.stringify({ platform, token }) });
  }

  notifications(): Promise<{ notifications: NotificationItem[] }> {
    return this.request('/v1/users/me/notifications');
  }

  markNotificationsRead(ids?: number[]): Promise<{ ok: true }> {
    return this.request('/v1/users/me/notifications/read', { method: 'POST', body: JSON.stringify({ ids }) });
  }

  meta(event: MetaEventRequest): Promise<{ ok: true }> {
    return this.request('/v1/users/me/meta', { method: 'POST', body: JSON.stringify(event) });
  }

  deleteAccount(): Promise<{ deleted: boolean; tombstoneId: number }> {
    return this.request('/v1/users/me', { method: 'DELETE' });
  }

  // --- drawing ---
  draw(body: { tile: string; lat: number; lng: number; accuracy: number; mockFlag: boolean; ops: DrawOp[]; tzOffsetMinutes: number }): Promise<DrawResponse> {
    return this.request('/v1/draw', { method: 'POST', body: JSON.stringify(body) });
  }

  undo(tile: string, idx: number): Promise<{ reverted: boolean; colour: number; ink: InkState }> {
    return this.request('/v1/undo', { method: 'POST', body: JSON.stringify({ tile, idx }) });
  }

  report(tile: string, region: TileRegion, reason: ReportReason, note?: string): Promise<{ id: number }> {
    return this.request('/v1/reports', { method: 'POST', body: JSON.stringify({ tile, region, reason, note }) });
  }
}

export const api = new ApiClient();
